import { useState, useRef } from "react";
import axios from "axios";
import { Upload, FileDown, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { Badge } from "../../components/ui/badge";
import { safeConfirm } from '../../lib/confirm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDataMigration() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/admin/migration/patients/template`, {
        responseType: "blob",
        withCredentials: true,
      });
      // Firefox/Safari require the anchor to be attached to the DOM before
      // .click() will fire. And URL.revokeObjectURL must run AFTER the
      // browser has consumed the URL, hence the setTimeout.
      const blobUrl = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = "physiogenic_patients_template.csv";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      }, 1000);
    } catch (e) {
      const detail = e.response?.data instanceof Blob
        ? await e.response.data.text().catch(() => "")
        : (e.response?.data?.detail || e.message);
      setError(`Failed to download template: ${detail || "unknown error"}`);
    }
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setPreview(null);
    setImportResult(null);
    setError(null);
  };

  const validatePreview = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/migration/patients/preview`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  const confirmImport = async () => {
    if (!file) return;
    if (!await safeConfirm(
      `Import ${preview.valid_count} valid patient row(s) into your clinic?\n` +
      `${preview.error_count} row(s) with errors will be SKIPPED.`
    )) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/admin/migration/patients/import`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Import failed");
    } finally {
      setLoading(false);
    }
  };

  const resetAll = () => {
    setFile(null);
    setPreview(null);
    setImportResult(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="space-y-6" data-testid="admin-data-migration">
      <div>
        <h1 className="text-3xl font-bold">Data migration</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Bring your existing client base into Physiogenic. Download the
          template, fill it in with one patient per row, then upload to
          validate &amp; import. Migrated patients keep all their
          demographics and medical history but won&apos;t be able to log in
          until you send them an invite.
        </p>
      </div>

      {/* Step 1 — Template */}
      <Card data-testid="migration-step-template">
        <CardHeader>
          <CardTitle className="text-base">Step 1 · Download the CSV template</CardTitle>
        </CardHeader>
        <CardContent>
          <Button onClick={downloadTemplate} data-testid="migration-template-btn"
                  variant="outline" className="gap-2">
            <FileDown className="h-4 w-4" />
            Download patients template
          </Button>
          <p className="text-xs text-muted-foreground mt-2">
            Required columns: <code>full_name</code>, <code>email</code>, <code>phone</code>.
            The rest are optional demographic + clinical fields.
          </p>
        </CardContent>
      </Card>

      {/* Step 2 — Upload */}
      <Card data-testid="migration-step-upload">
        <CardHeader>
          <CardTitle className="text-base">Step 2 · Upload your filled CSV</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={onFileChange}
            data-testid="migration-file-input"
            className="block text-sm file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground hover:file:opacity-90"
          />
          {file && (
            <div className="flex items-center gap-3 text-sm">
              <Badge variant="secondary" data-testid="migration-file-name">{file.name}</Badge>
              <span className="text-muted-foreground">
                {(file.size / 1024).toFixed(1)} KB
              </span>
              <Button onClick={validatePreview} disabled={loading} size="sm"
                      data-testid="migration-validate-btn" className="gap-2">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Validate &amp; preview
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Error banner */}
      {error && (
        <Alert variant="destructive" data-testid="migration-error">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Validation failed</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Step 3 — Preview */}
      {preview && !importResult && (
        <Card data-testid="migration-step-preview">
          <CardHeader>
            <CardTitle className="text-base">Step 3 · Review</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-3">
              <Badge variant="default" data-testid="migration-valid-count">
                {preview.valid_count} valid
              </Badge>
              <Badge variant={preview.error_count ? "destructive" : "secondary"}
                     data-testid="migration-error-count">
                {preview.error_count} errors
              </Badge>
            </div>

            {preview.errors && preview.errors.length > 0 && (
              <div className="border border-destructive/30 rounded-md p-3 max-h-48 overflow-y-auto text-xs space-y-1"
                   data-testid="migration-errors-list">
                <div className="font-semibold mb-1">Errors (rows skipped on import):</div>
                {preview.errors.slice(0, 50).map((e, i) => (
                  <div key={i} className="text-destructive">
                    Row {e.line} · <code>{e.field}</code> · {e.msg}
                  </div>
                ))}
                {preview.errors.length > 50 && (
                  <div className="text-muted-foreground">… and {preview.errors.length - 50} more</div>
                )}
              </div>
            )}

            {preview.preview && preview.preview.length > 0 && (
              <div className="overflow-x-auto border rounded-md" data-testid="migration-preview-table">
                <table className="text-xs w-full">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-2 py-1 text-left">Name</th>
                      <th className="px-2 py-1 text-left">Email</th>
                      <th className="px-2 py-1 text-left">Phone</th>
                      <th className="px-2 py-1 text-left">DOB</th>
                      <th className="px-2 py-1 text-left">Country</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.preview.map((r, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-2 py-1">{r.full_name}</td>
                        <td className="px-2 py-1">{r.email}</td>
                        <td className="px-2 py-1">{r.phone}</td>
                        <td className="px-2 py-1">{r.date_of_birth || "—"}</td>
                        <td className="px-2 py-1">{r.country || "PL"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex gap-2">
              <Button onClick={confirmImport}
                      disabled={loading || preview.valid_count === 0}
                      data-testid="migration-import-btn"
                      className="gap-2">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Confirm import ({preview.valid_count} rows)
              </Button>
              <Button onClick={resetAll} variant="outline" data-testid="migration-cancel-btn">
                Start over
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4 — Result */}
      {importResult && (
        <Card data-testid="migration-step-result">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              Step 4 · Done
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-3 gap-3 max-w-md">
              <div className="border rounded-md p-3 text-center">
                <div className="text-2xl font-bold text-green-600"
                     data-testid="migration-result-imported">
                  {importResult.imported}
                </div>
                <div className="text-xs text-muted-foreground">Imported</div>
              </div>
              <div className="border rounded-md p-3 text-center">
                <div className="text-2xl font-bold text-amber-600"
                     data-testid="migration-result-failed">
                  {importResult.failed}
                </div>
                <div className="text-xs text-muted-foreground">Skipped</div>
              </div>
              <div className="border rounded-md p-3 text-center">
                <div className="text-2xl font-bold text-destructive"
                     data-testid="migration-result-errors">
                  {importResult.validation_errors}
                </div>
                <div className="text-xs text-muted-foreground">Errors</div>
              </div>
            </div>

            {importResult.batch_tag && (
              <p className="text-xs text-muted-foreground">
                Find them on the Patients page filtered by batch tag:&nbsp;
                <code>{importResult.batch_tag}</code>
              </p>
            )}

            {importResult.failed_rows && importResult.failed_rows.length > 0 && (
              <div className="border border-amber-300/40 rounded-md p-3 text-xs space-y-1 max-h-40 overflow-y-auto">
                <div className="font-semibold mb-1">Skipped (already in your clinic):</div>
                {importResult.failed_rows.map((r, i) => (
                  <div key={i}>{r.email} — {r.reason}</div>
                ))}
              </div>
            )}

            <Button onClick={resetAll} variant="outline" data-testid="migration-done-btn">
              Migrate another file
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Footer help text */}
      <Card className="bg-muted/30">
        <CardContent className="pt-6 text-xs text-muted-foreground space-y-1">
          <div>• Max 5,000 rows / 5 MB per upload.</div>
          <div>• Duplicate emails within your clinic are skipped (not overwritten).</div>
          <div>• Imported patients are tagged <code>imported: true</code> in the database and grouped by batch tag.</div>
          <div>• Appointments / clinical notes / invoices migration coming next — patients first.</div>
        </CardContent>
      </Card>
    </div>
  );
}
